import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthContext';

/** The two callbacks every authenticated request needs. */
export function useApi() {
  const { getToken, refresh } = useAuth();
  return useMemo(() => ({ getToken, doRefresh: refresh }), [getToken, refresh]);
}

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** Refetch, e.g. after recording a session changes the numbers. */
  reload: () => void;
}

/**
 * Loads data on mount and whenever `deps` change.
 *
 * The late-response guard matters more here than in most apps: moving quickly
 * through chapter → section → next section fires overlapping requests, and
 * without the sequence check a slow earlier response can land last and paint
 * the previous section's content over the current one.
 */
export function useAsync<T>(load: () => Promise<T>, deps: unknown[]): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const sequence = useRef(0);

  // `load` is typically an inline arrow, so it is a new function every render.
  // Keeping it in a ref lets the effect depend on `deps` alone rather than
  // refetching on every render.
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    const current = ++sequence.current;
    setLoading(true);
    setError(null);

    loadRef
      .current()
      .then((result) => {
        if (sequence.current !== current) return;
        setData(result);
      })
      .catch((err: unknown) => {
        if (sequence.current !== current) return;
        setError(err instanceof Error ? err.message : 'Something went wrong');
      })
      .finally(() => {
        if (sequence.current !== current) return;
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { data, loading, error, reload };
}
